import React, { useState, useMemo } from 'react';
import { useFormik } from 'formik';
import { PaymentData, SimpliPayConfig, CardDetails } from '../types';
import { cardFormSchema, formatCardNumber, formatExpiryDate, detectCardType, CardType } from '../utils/validation';

interface CardFormProps {
  paymentData: PaymentData;
  config: SimpliPayConfig;
  setCardDetails: (details: CardDetails) => void;
  setStep: (step: any) => void;
  onClose: () => void;
}

const CardForm: React.FC<CardFormProps> = ({
  paymentData,
  setCardDetails,
  setStep,
  onClose,
}) => {
  const [cardType, setCardType] = useState<CardType>('unknown');

  const formik = useFormik({
    initialValues: { pan: '', expiryDate: '', cvv: '' },
    validationSchema: cardFormSchema,
    onSubmit: (values) => {
      setCardDetails({
        pan: values.pan.replace(/\s/g, ''),
        expiryDate: values.expiryDate,
        cvv: values.cvv,
      });
      setStep('pin');
    },
  });

  const handlePanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 19) {
      formik.setFieldValue('pan', formatted);
      setCardType(detectCardType(formatted));
    }
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    if (cleaned.length <= 4) {
      formik.setFieldValue('expiryDate', formatExpiryDate(e.target.value));
    }
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      formik.setFieldValue('cvv', value);
    }
  };

  const isFormValid = useMemo(() => formik.isValid && formik.dirty, [formik.isValid, formik.dirty]);

  return (
    <div className="simplipay-card-form">
      <div className="simplipay-header">
        <img
          className="simplipay-logo"
          src="https://c.animaapp.com/mGNLCcRC/img/simplipay-logo.svg"
          alt="SimpliPay"
        />
        <div className="simplipay-info">
          <span className="simplipay-email">{paymentData.email}</span>
          <span className="simplipay-amount">
            Pay {paymentData.currency} {paymentData.amount.toLocaleString()}
          </span>
        </div>
        <p className="simplipay-subtitle">Provide card details to make payment</p>
      </div>

      <form onSubmit={formik.handleSubmit} className="simplipay-form">
        <div className="simplipay-form-group">
          <label>Card Number <span className="required">*</span></label>
          <div className={`simplipay-input-wrapper ${formik.touched.pan && formik.errors.pan ? 'error' : ''}`}>
            <input
              name="pan"
              type="text"
              inputMode="numeric"
              placeholder="0000 0000 0000 0000"
              value={formik.values.pan}
              onChange={handlePanChange}
              onBlur={formik.handleBlur}
              autoComplete="cc-number"
            />
            <span className="card-type">{cardType !== 'unknown' ? cardType.toUpperCase() : ''}</span>
          </div>
          {formik.touched.pan && formik.errors.pan && (
            <span className="error-text">{formik.errors.pan}</span>
          )}
        </div>

        <div className="simplipay-form-row">
          <div className="simplipay-form-group half">
            <label>Expiry Date <span className="required">*</span></label>
            <div className={`simplipay-input-wrapper ${formik.touched.expiryDate && formik.errors.expiryDate ? 'error' : ''}`}>
              <input
                name="expiryDate"
                type="text"
                inputMode="numeric"
                placeholder="MM / YY"
                value={formik.values.expiryDate}
                onChange={handleExpiryChange}
                onBlur={formik.handleBlur}
                autoComplete="cc-exp"
              />
            </div>
            {formik.touched.expiryDate && formik.errors.expiryDate && (
              <span className="error-text">{formik.errors.expiryDate}</span>
            )}
          </div>

          <div className="simplipay-form-group half">
            <label>CVV <span className="required">*</span></label>
            <div className={`simplipay-input-wrapper ${formik.touched.cvv && formik.errors.cvv ? 'error' : ''}`}>
              <input
                name="cvv"
                type="password"
                inputMode="numeric"
                placeholder="123"
                maxLength={4}
                value={formik.values.cvv}
                onChange={handleCvvChange}
                onBlur={formik.handleBlur}
                autoComplete="cc-csc"
              />
            </div>
            {formik.touched.cvv && formik.errors.cvv && (
              <span className="error-text">{formik.errors.cvv}</span>
            )}
          </div>
        </div>

        <button type="submit" className="simplipay-button" disabled={!isFormValid}>
          Pay {paymentData.currency} {paymentData.amount.toLocaleString()}
        </button>
      </form>

      <div className="simplipay-footer">
        <span>🔒 Secured by <strong>SimpliPay</strong></span>
      </div>
    </div>
  );
};

export default CardForm;
